import { useAuth } from '@clerk/clerk-react';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FigClient } from '../core/api/client.js';
import { FigSession, type SignOutReason } from '../core/auth/session.js';
import type { RoleClaim } from '../core/api/types.js';
import { browserSessionStorage } from './storage.js';
import { audioStore } from './audioStore.js';
import { trackRequest, setTelemetryContext, trackError } from '../lib/telemetry.js';
import { webCryptoDecryptor } from '../core/api/decrypt.js';
import { usageKey, useUsageReporting } from './usage.js';

interface FigContextValue {
  readonly client: FigClient;
  readonly session: FigSession;
  readonly role: RoleClaim | null;
  /** Why the session ended, so the sign-in screen can say something specific. */
  readonly signedOutReason: SignOutReason | null;
  readonly ready: boolean;
}

const FigContext = createContext<FigContextValue | null>(null);

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export function FigProvider({ children }: { children: ReactNode }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [role, setRole] = useState<RoleClaim | null>(null);
  const [signedOutReason, setSignedOutReason] = useState<SignOutReason | null>(null);
  const [ready, setReady] = useState(false);

  // Clerk hands back a new `getToken` identity on most renders. Holding it in a ref keeps the
  // FigSession stable for the life of the app - rebuilding it would throw away the stored
  // token and re-exchange on every render.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const { session, client } = useMemo(() => {
    const s = new FigSession({
      apiBaseUrl: API_BASE,
      // Clerk's own token lives 60 seconds, so it is fetched fresh here every single time
      // rather than cached anywhere.
      getClerkToken: () => getTokenRef.current(),
      storage: browserSessionStorage,
      onSignedOut: (reason) => {
        setSignedOutReason(reason);
        setRole(null);
        // Cached recordings must not outlive the session that was allowed to hear them.
        audioStore.clear();
      },
    });
    // `onRequest` is the whole of client-side latency measurement for the portal, in one place —
    // the agency's end clients are the people whose experience we otherwise never hear about.
    return {
      session: s,
      client: new FigClient({
        baseUrl: API_BASE, session: s, audioStore, onRequest: trackRequest,
        // crypto.subtle is native here, so this costs nothing and adds no dependency.
        recordingDecryptor: webCryptoDecryptor,
      }),
    };
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;
    (async () => {
      if (!isSignedIn) { await session.signOut().catch(() => {}); if (!cancelled) setReady(true); return; }
      try {
        await session.getToken();
        if (!cancelled) {
          setRole(session.role);
          setSignedOutReason(null);
          // The role, never the identity: enough to tell an agency admin's slow page from an end
          // client's, which is a real distinction, without carrying who they are.
          setTelemetryContext({
            role: session.role ?? undefined,
            // Our own accounts and the demo tour, so the ops dashboard can count customers
            // separately (2026-10-21). A flag, never an identity.
            internal: String(session.isInternal || session.isDemo),
            sessionId: usageKey(),
          });
        }
      } catch (err) {
        // onSignedOut has already recorded the reason; the router sends them to sign-in. Recorded
        // in telemetry too (2026-10-21): a Clerk sign-in that Fig refuses was invisible before.
        trackError(err, { where: 'session-exchange' });
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, session]);

  // Time in the portal, for a signed-in person only.
  useUsageReporting(role ? client : null, onNewVisit);

  const value = useMemo(
    () => ({ client, session, role, signedOutReason, ready: ready && isLoaded }),
    [client, session, role, signedOutReason, ready, isLoaded],
  );
  return <FigContext.Provider value={value}>{children}</FigContext.Provider>;
}

/** A new visit gets a new App Insights session, so the dashboard counts visits the same way. */
const onNewVisit = (key: string) => setTelemetryContext({ sessionId: key });

export function useFig(): FigContextValue {
  const v = useContext(FigContext);
  if (!v) throw new Error('useFig must be used inside <FigProvider>');
  return v;
}
